import type { SupabaseClient } from "@supabase/supabase-js";
import type {
  OfflineApplyResult,
  OfflineChecklistItem,
  OfflineEvidence,
  OfflineMissionBundle,
  OfflineMissionGateway,
  OfflineMissionMutation,
  OfflineMissionRole,
} from "./offline-missions";

type JsonRecord = Record<string, unknown>;

export type PreparedOfflineEvidence = {
  storagePath: string;
  originalName: string;
  mimeType: string;
  sizeBytes: number;
};

export interface OfflineEvidenceTransfer {
  prepareEvidence(mutation: OfflineMissionMutation): Promise<PreparedOfflineEvidence>;
  releaseEvidence?(mutation: OfflineMissionMutation): Promise<void>;
}

export interface OfflineEvidenceFileSource {
  getEvidenceFile(evidenceId: string, mutation: OfflineMissionMutation): Promise<{
    blob: Blob;
    mimeType: string;
    originalName: string;
  } | null>;
  removeEvidenceFile?(evidenceId: string, mutation: OfflineMissionMutation): Promise<void>;
}

function asRecord(value: unknown, message: string): JsonRecord {
  if (!value || typeof value !== "object" || Array.isArray(value)) {
    throw new Error(message);
  }
  return value as JsonRecord;
}

function asArray(value: unknown) {
  return Array.isArray(value) ? value : [];
}

function requiredString(value: unknown, message: string) {
  if (typeof value !== "string" || !value.trim()) throw new Error(message);
  return value;
}

function nullableString(value: unknown) {
  return typeof value === "string" && value.trim() ? value : null;
}

function numberValue(value: unknown, fallback = 0) {
  const parsed = typeof value === "number" ? value : Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
}

function mapActivity(value: unknown) {
  const activity = asRecord(value, "Une activité téléchargée est invalide.");
  return {
    activityKey: requiredString(activity.activityKey, "Une activité n’a pas d’identifiant."),
    activityLabel: requiredString(activity.activityLabel, "Une activité n’a pas de libellé."),
  };
}

function mapInventoryItem(value: unknown) {
  const item = asRecord(value, "Un produit téléchargé est invalide.");
  return {
    id: requiredString(item.id, "Un produit n’a pas d’identifiant."),
    name: requiredString(item.name, "Un produit n’a pas de nom."),
    category: nullableString(item.category),
    unit: requiredString(item.unit, "Un produit n’a pas d’unité."),
    itemKind: requiredString(item.itemKind, "Un produit n’a pas de type."),
    currentQuantity: numberValue(item.currentQuantity),
  };
}

function mapEvidence(value: unknown, checklistItemId: string): OfflineEvidence {
  const evidence = asRecord(value, "Une preuve téléchargée est invalide.");
  return {
    id: requiredString(evidence.id, "Une preuve téléchargée n’a pas d’identifiant."),
    checklistItemId,
    localUri: "",
    mimeType: requiredString(evidence.mime_type, "Une preuve téléchargée n’a pas de type."),
    capturedAt: requiredString(evidence.created_at, "Une preuve téléchargée n’a pas de date."),
    remoteStoragePath: requiredString(
      evidence.storage_path,
      "Une preuve téléchargée n’a pas de chemin sécurisé.",
    ),
  };
}

function mapChecklistItem(value: unknown): {
  item: OfflineChecklistItem;
  evidence: OfflineEvidence[];
} {
  const item = asRecord(value, "Une étape de checklist téléchargée est invalide.");
  const id = requiredString(item.id, "Une étape de checklist n’a pas d’identifiant.");
  return {
    item: {
      id,
      label: requiredString(item.label, "Une étape de checklist n’a pas de libellé."),
      instructions: nullableString(item.instructions),
      required: item.is_required === true,
      minimumPhotoCount: Math.max(0, numberValue(item.minimum_photo_count)),
      completed: item.is_completed === true,
    },
    evidence: asArray(item.evidence).map((entry) => mapEvidence(entry, id)),
  };
}

function mapMission(
  value: unknown,
  expected: { profileId: string; role: OfflineMissionRole; workDate: string },
  downloadedAt: string,
): OfflineMissionBundle {
  const mission = asRecord(value, "Une mission hors ligne téléchargée est invalide.");
  if (mission.profileId !== expected.profileId || mission.role !== expected.role) {
    throw new Error("Le serveur a retourné une mission hors du profil autorisé.");
  }
  if (mission.workDate !== expected.workDate) {
    throw new Error("Le serveur a retourné une mission hors de la journée demandée.");
  }
  const checklist = asRecord(
    mission.checklist ?? { available: false, items: [] },
    "La checklist téléchargée est invalide.",
  );
  const mappedItems = asArray(checklist.items).map(mapChecklistItem);
  const reporting = asRecord(
    mission.reporting ?? { mode: "individual", assignedHours: 0, members: [] },
    "Le contexte de compte rendu téléchargé est invalide.",
  );
  const reportingMode = reporting.mode;
  if (!["individual", "team_leader", "team_member"].includes(String(reportingMode))) {
    throw new Error("Le mode de compte rendu téléchargé est invalide.");
  }

  return {
    missionId: requiredString(mission.missionId, "Une mission téléchargée n’a pas d’identifiant."),
    organizationId: requiredString(
      mission.organizationId,
      "Une mission téléchargée n’a pas d’organisation.",
    ),
    profileId: expected.profileId,
    role: expected.role,
    serverVersion: requiredString(
      mission.serverVersion,
      "Une mission téléchargée n’a pas de version serveur.",
    ),
    workDate: expected.workDate,
    startsAt: requiredString(mission.startsAt, "Une mission téléchargée n’a pas d’heure de début."),
    endsAt: requiredString(mission.endsAt, "Une mission téléchargée n’a pas d’heure de fin."),
    location: requiredString(mission.location, "Une mission téléchargée n’a pas d’adresse."),
    clientDisplayName: nullableString(mission.clientDisplayName),
    contactName: nullableString(mission.contactName),
    contactPhone: nullableString(mission.contactPhone),
    instructions: nullableString(mission.instructions),
    checklistEditable: checklist.can_edit !== false,
    checklist: mappedItems.map((entry) => entry.item),
    evidence: mappedItems.flatMap((entry) => entry.evidence),
    reporting: {
      mode: reportingMode as "individual" | "team_leader" | "team_member",
      assignedHours: Math.max(0, numberValue(reporting.assignedHours)),
      members: asArray(reporting.members).map((entry) => {
        const member = asRecord(entry, "Un membre du binôme téléchargé est invalide.");
        return {
          workerId: requiredString(member.workerId, "Un membre du binôme n’a pas d’identifiant."),
          displayName: requiredString(member.displayName, "Un membre du binôme n’a pas de nom."),
          assignedHours: Math.max(0, numberValue(member.assignedHours)),
        };
      }),
    },
    activityCatalog: asArray(mission.activityCatalog).map(mapActivity),
    inventoryCatalog: asArray(mission.inventoryCatalog).map(mapInventoryItem),
    downloadedAt,
    lastSynchronizedAt: downloadedAt,
  };
}

function isBusinessRejection(code: string | undefined) {
  return code === "22023" || code === "23514" || code === "42501";
}

export class SupabaseOfflineMissionGateway implements OfflineMissionGateway {
  private readonly client: SupabaseClient;
  private readonly evidenceTransfer: OfflineEvidenceTransfer | null;
  private readonly now: () => string;

  constructor(
    client: SupabaseClient,
    options?: {
      evidenceTransfer?: OfflineEvidenceTransfer;
      now?: () => string;
    },
  ) {
    this.client = client;
    this.evidenceTransfer = options?.evidenceTransfer ?? null;
    this.now = options?.now ?? (() => new Date().toISOString());
  }

  async downloadMissionDay(input: {
    profileId: string;
    role: OfflineMissionRole;
    workDate: string;
  }) {
    const response = await this.client.rpc("get_offline_mission_day", {
      p_work_date: input.workDate,
    });
    if (response.error) throw new Error(response.error.message);
    const payload = asRecord(response.data, "La journée hors ligne reçue est invalide.");
    if (payload.profileId !== input.profileId || payload.role !== input.role) {
      throw new Error("Le serveur a retourné une journée hors du profil autorisé.");
    }
    if (payload.workDate !== input.workDate) {
      throw new Error("Le serveur a retourné une autre journée de travail.");
    }
    const downloadedAt = requiredString(
      payload.downloadedAt,
      "La journée hors ligne reçue n’a pas de date de téléchargement.",
    );
    return asArray(payload.missions).map((mission) =>
      mapMission(mission, input, downloadedAt),
    );
  }

  async applyMutation(mutation: OfflineMissionMutation): Promise<OfflineApplyResult> {
    if (![
      "checklist_item_set",
      "evidence_added",
      "report_draft_saved",
      "completion_requested",
    ].includes(mutation.kind)) {
      return {
        status: "rejected",
        reason: "Cette action hors ligne n’est pas encore activée côté serveur.",
      };
    }

    let payload = mutation.payload;
    let preparedEvidence = false;
    if (mutation.kind === "evidence_added" && !payload.storagePath) {
      if (!this.evidenceTransfer) {
        return {
          status: "rejected",
          reason: "Le transfert sécurisé de la photo n’est pas disponible sur cet appareil.",
        };
      }
      payload = {
        ...payload,
        ...(await this.evidenceTransfer.prepareEvidence(mutation)),
      };
      preparedEvidence = true;
    }

    const rpcName = ["report_draft_saved", "completion_requested"].includes(mutation.kind)
      ? "apply_mission_offline_report_mutation"
      : "apply_mission_offline_mutation";
    const response = await this.client.rpc(rpcName, {
      p_mission_id: mutation.missionId,
      p_client_mutation_key: mutation.idempotencyKey,
      p_mutation_kind: mutation.kind,
      p_payload: payload,
      p_server_version: mutation.serverVersion,
    });
    if (response.error) {
      if (isBusinessRejection(response.error.code)) {
        return { status: "rejected", reason: response.error.message };
      }
      throw new Error(response.error.message);
    }

    const result = asRecord(response.data, "La réponse de synchronisation est invalide.");
    if (result.status === "conflict") {
      return {
        status: "conflict",
        reason:
          result.reason === "mission_changed"
            ? "La mission a changé depuis son téléchargement. Rechargez-la avant de poursuivre."
            : nullableString(result.reason) ?? "La mission doit être rechargée.",
      };
    }
    if (result.status !== "acknowledged") {
      return { status: "rejected", reason: "La modification n’a pas été confirmée par le serveur." };
    }
    if (preparedEvidence && this.evidenceTransfer?.releaseEvidence) {
      await this.evidenceTransfer.releaseEvidence(mutation);
    }
    return { status: "acknowledged", synchronizedAt: this.now() };
  }
}

function evidenceExtension(mimeType: string) {
  if (mimeType === "image/jpeg") return "jpg";
  if (mimeType === "image/png") return "png";
  if (mimeType === "image/webp") return "webp";
  throw new Error("La preuve doit être une photo JPEG, PNG ou WebP.");
}

function safePathSegment(value: unknown, label: string) {
  const segment = requiredString(value, `${label} manquant.`);
  if (!/^[A-Za-z0-9_-]+$/.test(segment)) throw new Error(`${label} invalide.`);
  return segment;
}

export function buildOfflineEvidenceStoragePath(mutation: OfflineMissionMutation) {
  const mimeType = requiredString(mutation.payload.mimeType, "Type de photo manquant.");
  const itemId = safePathSegment(mutation.payload.checklistItemId, "Étape de checklist");
  const evidenceId = safePathSegment(
    mutation.payload.evidenceId ?? mutation.id,
    "Identifiant de photo",
  );
  return [
    safePathSegment(mutation.organizationId, "Organisation"),
    safePathSegment(mutation.profileId, "Profil"),
    "offline",
    safePathSegment(mutation.missionId, "Mission"),
    itemId,
    `${evidenceId}.${evidenceExtension(mimeType)}`,
  ].join("/");
}

export class BrowserSupabaseOfflineEvidenceTransfer implements OfflineEvidenceTransfer {
  private readonly client: SupabaseClient;
  private readonly fetchFile: typeof fetch;
  private readonly fileSource: OfflineEvidenceFileSource | null;

  constructor(
    client: SupabaseClient,
    fetchFile: typeof fetch = fetch,
    fileSource: OfflineEvidenceFileSource | null = null,
  ) {
    this.client = client;
    this.fetchFile = fetchFile;
    this.fileSource = fileSource;
  }

  async prepareEvidence(mutation: OfflineMissionMutation): Promise<PreparedOfflineEvidence> {
    if (mutation.kind !== "evidence_added") {
      throw new Error("Cette modification ne contient pas de photo.");
    }
    const evidenceId = safePathSegment(
      mutation.payload.evidenceId ?? mutation.id,
      "Identifiant de photo",
    );
    const mimeType = requiredString(mutation.payload.mimeType, "Type de photo manquant.");
    const storedFile = this.fileSource
      ? await this.fileSource.getEvidenceFile(evidenceId, mutation)
      : null;
    let blob: Blob;
    if (storedFile) {
      if (storedFile.mimeType !== mimeType) {
        throw new Error("Le type de la photo persistée ne correspond pas au type annoncé.");
      }
      blob = storedFile.blob;
    } else {
      const localUri = requiredString(mutation.payload.localUri, "Fichier photo local manquant.");
      const response = await this.fetchFile(localUri);
      if (!response.ok) throw new Error("La photo locale ne peut pas être relue.");
      blob = await response.blob();
    }
    if (blob.size <= 0 || blob.size > 10_485_760) {
      throw new Error("La photo doit peser entre 1 octet et 10 Mo.");
    }
    if (blob.type && blob.type !== mimeType) {
      throw new Error("Le type réel de la photo locale ne correspond pas au type annoncé.");
    }
    const storagePath = buildOfflineEvidenceStoragePath(mutation);
    const extension = evidenceExtension(mimeType);
    const originalName =
      nullableString(mutation.payload.originalName) ??
      nullableString(storedFile?.originalName) ??
      `preuve-${evidenceId}.${extension}`;
    const upload = await this.client.storage
      .from("mission-evidence")
      .upload(storagePath, blob, { contentType: mimeType, upsert: false });
    const duplicate =
      upload.error &&
      (String((upload.error as { statusCode?: string }).statusCode ?? "") === "409" ||
        /already exists|duplicate/i.test(upload.error.message));
    if (upload.error && !duplicate) throw new Error(upload.error.message);

    return {
      storagePath,
      originalName,
      mimeType,
      sizeBytes: blob.size,
    };
  }

  async releaseEvidence(mutation: OfflineMissionMutation) {
    if (!this.fileSource?.removeEvidenceFile) return;
    const evidenceId = safePathSegment(
      mutation.payload.evidenceId ?? mutation.id,
      "Identifiant de photo",
    );
    await this.fileSource.removeEvidenceFile(evidenceId, mutation);
  }
}
